'use client'
import { useMemo, useCallback, useState } from 'react'
import {
  PlusIcon,
  MinusIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/16/solid'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { cn } from '../../helpers/classnames'
import { JosefSwitch } from '../switch'
import { JosefSelect } from '../select'

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

type Day = (typeof DAYS)[number]

export interface BusinessHoursSlot {
  open: string[] | null
  close: string[] | null
}

export type BusinessHoursValue = Partial<Record<Day, BusinessHoursSlot>>

export interface JosefBusinessHoursProps {
  value: BusinessHoursValue
  onChange: (value: BusinessHoursValue) => void
  interval?: number
  className?: string
}

const DEFAULT_OPEN = '1970-01-01T09:00:00.000Z'
const DEFAULT_CLOSE = '1970-01-01T17:00:00.000Z'

function generateTimeOptions(intervalSeconds: number) {
  const options: { value: string; label: string }[] = []
  const intervalMinutes = intervalSeconds / 60
  const totalSlots = Math.floor((24 * 60) / intervalMinutes) + 1

  for (let i = 0; i < totalSlots; i++) {
    const totalMinutes = i * intervalMinutes
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours > 24) break
    if (hours === 24 && minutes > 0) break

    const label = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    const isoDate = new Date(Date.UTC(1970, 0, 1, hours % 24, minutes, 0, 0))
    const value = isoDate.toISOString()

    options.push({ value, label })
  }

  return options
}

function isoToMinutes(iso: string): number {
  const date = new Date(iso)
  return date.getUTCHours() * 60 + date.getUTCMinutes()
}

function minutesToLabel(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

interface TimeSlotRowProps {
  openValue: string
  closeValue: string
  previousCloseValue?: string
  onOpenChange: (value: string) => void
  onCloseChange: (value: string) => void
  onRemove: () => void
  onAdd: () => void
  canRemove: boolean
  canAdd: boolean
  isLast: boolean
  timeOptions: { value: string; label: string }[]
}

function TimeSlotRow({
  openValue,
  closeValue,
  previousCloseValue,
  onOpenChange,
  onCloseChange,
  onRemove,
  onAdd,
  canRemove,
  canAdd,
  isLast,
  timeOptions,
}: TimeSlotRowProps) {
  const openMinutes = isoToMinutes(openValue)
  const previousCloseMinutes = previousCloseValue
    ? isoToMinutes(previousCloseValue)
    : 0

  const filteredOpenOptions = previousCloseValue
    ? timeOptions.filter((opt) => isoToMinutes(opt.value) > previousCloseMinutes)
    : timeOptions

  const filteredCloseOptions = timeOptions.filter(
    (opt) => isoToMinutes(opt.value) >= openMinutes
  )

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 flex items-center gap-2">
        <JosefSelect
          variant="borderless"
          size="sm"
          options={filteredOpenOptions}
          value={openValue}
          onChange={onOpenChange}
          className="flex-1"
        />
        <span className="text-xs text-black/50 dark:text-white/50">to</span>
        <JosefSelect
          variant="borderless"
          size="sm"
          options={filteredCloseOptions}
          value={closeValue}
          onChange={onCloseChange}
          className="flex-1"
        />
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className={cn(
            'p-1.5 rounded-md transition-colors',
            canRemove
              ? 'hover:bg-black/5 dark:hover:bg-white/5 text-black/50 dark:text-white/50'
              : 'opacity-30 cursor-not-allowed text-black/30 dark:text-white/30'
          )}
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        {isLast ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={!canAdd}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              canAdd
                ? 'hover:bg-black/5 dark:hover:bg-white/5 text-black/50 dark:text-white/50'
                : 'opacity-30 cursor-not-allowed text-black/30 dark:text-white/30'
            )}
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        ) : (
          <div className="w-7" />
        )}
      </div>
    </div>
  )
}

interface DayCardProps {
  day: Day
  hours: BusinessHoursSlot | undefined
  onChange: (hours: BusinessHoursSlot) => void
  onCopyFrom: (sourceDay: Day) => void
  allHours: BusinessHoursValue
  timeOptions: { value: string; label: string }[]
}

function DayCard({
  day,
  hours,
  onChange,
  onCopyFrom,
  allHours,
  timeOptions,
}: DayCardProps) {
  const isClosed = !hours?.open?.length || !hours?.close?.length

  const daysWithHours = DAYS.filter((d) => {
    if (d === day) return false
    const h = allHours[d]
    return h?.open?.length && h?.close?.length
  })

  const slots =
    isClosed || !hours?.open || !hours?.close
      ? []
      : hours.open.map((openTime, index) => ({
          open: openTime,
          close: hours.close?.[index] ?? DEFAULT_CLOSE,
        }))

  const handleToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChange({
          open: [DEFAULT_OPEN],
          close: [DEFAULT_CLOSE],
        })
      } else {
        onChange({
          open: null,
          close: null,
        })
      }
    },
    [onChange]
  )

  const handleSlotChange = useCallback(
    (index: number, field: 'open' | 'close', value: string) => {
      if (!hours?.open || !hours?.close) return

      const newOpen = [...hours.open]
      const newClose = [...hours.close]

      if (field === 'open') {
        newOpen[index] = value
        const openMinutes = isoToMinutes(value)
        const closeMinutes = isoToMinutes(newClose[index])
        if (closeMinutes < openMinutes) {
          const newCloseMinutes = openMinutes + 60
          const newCloseHours = Math.floor(newCloseMinutes / 60)
          const newCloseMins = newCloseMinutes % 60
          const newCloseDate = new Date(
            Date.UTC(1970, 0, 1, newCloseHours % 24, newCloseMins, 0, 0)
          )
          newClose[index] = newCloseDate.toISOString()
        }
      } else {
        newClose[index] = value
      }

      onChange({ open: newOpen, close: newClose })
    },
    [hours, onChange]
  )

  const handleAddSlot = useCallback(() => {
    if (!hours?.open || !hours?.close) return

    const lastClose = hours.close[hours.close.length - 1]
    const lastCloseMinutes = isoToMinutes(lastClose)

    const newOpenMinutes = lastCloseMinutes + 30
    const remainingMinutes = 24 * 60 - newOpenMinutes
    const newCloseMinutes = Math.min(newOpenMinutes + 60, 24 * 60)

    if (remainingMinutes < 30) return

    const newOpenHours = Math.floor(newOpenMinutes / 60)
    const newOpenMins = newOpenMinutes % 60
    const newCloseHours = Math.floor(newCloseMinutes / 60)
    const newCloseMins = newCloseMinutes % 60

    const newOpenDate = new Date(
      Date.UTC(1970, 0, 1, newOpenHours % 24, newOpenMins, 0, 0)
    )
    const newCloseDate = new Date(
      Date.UTC(1970, 0, 1, newCloseHours % 24, newCloseMins, 0, 0)
    )

    onChange({
      open: [...hours.open, newOpenDate.toISOString()],
      close: [...hours.close, newCloseDate.toISOString()],
    })
  }, [hours, onChange])

  const handleRemoveSlot = useCallback(
    (index: number) => {
      if (!hours?.open || !hours?.close || hours.open.length <= 1) return

      onChange({
        open: hours.open.filter((_, i) => i !== index),
        close: hours.close.filter((_, i) => i !== index),
      })
    },
    [hours, onChange]
  )

  const canAddSlot = (() => {
    if (!hours?.close?.length) return false
    const lastClose = hours.close[hours.close.length - 1]
    const lastCloseMinutes = isoToMinutes(lastClose)
    return lastCloseMinutes < 24 * 60 - 30
  })()

  return (
    <div className="rounded-lg bg-neutral-100/50 dark:bg-neutral-800/30 px-3">
      <div className="flex items-center justify-between py-2.5">
        <span className="text-sm font-medium text-black/70 dark:text-white/70 capitalize">
          {day}
        </span>
        <div className="flex items-center gap-2">
          {daysWithHours.length > 0 ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 px-2 py-1 text-xs text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded transition-colors"
                >
                  <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                  Copy from...
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[120px] bg-white dark:bg-[#1a1a1a] rounded-lg shadow-lg border border-black/10 dark:border-white/10 p-1 z-50"
                  sideOffset={5}
                  align="end"
                >
                  {daysWithHours.map((sourceDay) => (
                    <DropdownMenu.Item
                      key={sourceDay}
                      className="px-3 py-2 text-sm text-black dark:text-white rounded-md cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 outline-none capitalize"
                      onSelect={() => onCopyFrom(sourceDay)}
                    >
                      {sourceDay}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : null}
          <JosefSwitch checked={!isClosed} onChange={handleToggle} />
        </div>
      </div>
      {!isClosed && slots.length > 0 ? (
        <div className="pb-3 space-y-2">
          {slots.map((slot, index) => (
            <TimeSlotRow
              key={index}
              openValue={slot.open}
              closeValue={slot.close}
              previousCloseValue={index > 0 ? slots[index - 1].close : undefined}
              onOpenChange={(value) => handleSlotChange(index, 'open', value)}
              onCloseChange={(value) => handleSlotChange(index, 'close', value)}
              onRemove={() => handleRemoveSlot(index)}
              onAdd={handleAddSlot}
              canRemove={slots.length > 1}
              canAdd={canAddSlot}
              isLast={index === slots.length - 1}
              timeOptions={timeOptions}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function JosefBusinessHours({
  value,
  onChange,
  interval = 900,
  className,
}: JosefBusinessHoursProps) {
  const timeOptions = useMemo(() => generateTimeOptions(interval), [interval])

  const hasAnyOpen = DAYS.some((day) => {
    const h = value[day]
    return h?.open?.length && h?.close?.length
  })

  const handleDayChange = useCallback(
    (day: Day, hours: BusinessHoursSlot) => {
      onChange({
        ...value,
        [day]: hours,
      })
    },
    [value, onChange]
  )

  const handleCopyFrom = useCallback(
    (targetDay: Day, sourceDay: Day) => {
      const sourceHours = value[sourceDay]
      if (!sourceHours) return
      onChange({
        ...value,
        [targetDay]: {
          open: sourceHours.open ? [...sourceHours.open] : null,
          close: sourceHours.close ? [...sourceHours.close] : null,
        },
      })
    },
    [value, onChange]
  )

  const handleCloseAll = useCallback(() => {
    const closedHours: BusinessHoursValue = {}
    DAYS.forEach((day) => {
      closedHours[day] = { open: null, close: null }
    })
    onChange(closedHours)
  }, [onChange])

  const handleOpenAll = useCallback(() => {
    const openHours: BusinessHoursValue = {}
    DAYS.forEach((day) => {
      openHours[day] = { open: [DEFAULT_OPEN], close: [DEFAULT_CLOSE] }
    })
    onChange(openHours)
  }, [onChange])

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={hasAnyOpen ? handleCloseAll : handleOpenAll}
          className="px-3 py-1.5 text-xs font-medium rounded-md border border-black/10 dark:border-white/10 text-black/70 dark:text-white/70 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        >
          {hasAnyOpen ? 'Close all days' : 'Open all days'}
        </button>
      </div>
      {DAYS.map((day) => (
        <DayCard
          key={day}
          day={day}
          hours={value[day]}
          onChange={(hours) => handleDayChange(day, hours)}
          onCopyFrom={(sourceDay) => handleCopyFrom(day, sourceDay)}
          allHours={value}
          timeOptions={timeOptions}
        />
      ))}
    </div>
  )
}

export default JosefBusinessHours
